import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/terms_and_conditions_settings';

const INTEREST_PERIODS = ['annual', 'quarterly', 'monthly'] as const;
const PENALTY_CLAUSE_TYPES = ['percentage', 'fix'] as const;

// Read schemas are deliberately permissive about ranges. Only `name` is NOT NULL
// on the table and several columns carry no numeric validation, so rows predating
// the current rules can hold values the write path would refuse — an empty name,
// a negative delay, an out-of-range rate. Rejecting them here would blank the
// whole card instead of letting the user see and correct the row. The bounds are
// enforced on the way out instead, by the update hook and the form.
export const InterestSchema = z.object({
  value: z.number(),
  period: z.enum(INTEREST_PERIODS),
});

export const FixFeeSchema = z.object({
  value: z.number(),
});

export const PenaltyClauseSchema = z.object({
  type: z.enum(PENALTY_CLAUSE_TYPES),
  value: z.number(),
  minimum: z.number(),
});

// Each fee block is nullable rather than optional: the backend always sends the
// key, and null is what "this fee is switched off" looks like on the wire.
export const TermsAndConditionsSchema = z.object({
  id: z.number(),
  name: z.string(),
  default: z.boolean(),
  minimumPaymentTermsDelay: z.number().int(),
  useBelgianLaw: z.boolean(),
  interest: InterestSchema.nullable(),
  fixFee: FixFeeSchema.nullable(),
  penaltyClause: PenaltyClauseSchema.nullable(),
  hasPDF: z.boolean(),
  // Both empty when the set carries no document. The URL is signed and expiring.
  pdfFilename: z.string(),
  pdfUrl: z.string(),
  deletable: z.boolean(),
});

export const TermsAndConditionsSettingsSchema = z.object({
  termsAndConditions: z.array(TermsAndConditionsSchema),
  sendTOS: z.boolean(),
  ignoreLateFees: z.boolean(),
});

export type Interest = z.infer<typeof InterestSchema>;
export type FixFee = z.infer<typeof FixFeeSchema>;
export type PenaltyClause = z.infer<typeof PenaltyClauseSchema>;
export type TermsAndConditions = z.infer<typeof TermsAndConditionsSchema>;
export type TermsAndConditionsSettings = z.infer<typeof TermsAndConditionsSettingsSchema>;

export async function loadTermsAndConditionsSettingsQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = TermsAndConditionsSettingsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'termsAndConditionsSettings');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadTermsAndConditionsSettings(
  options: QueryOptions<TermsAndConditionsSettings> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<TermsAndConditionsSettings, ApiError>({
    queryKey: ['termsAndConditionsSettings'],
    queryFn: () => loadTermsAndConditionsSettingsQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<
    TermsAndConditionsSettings,
    ApiError,
    'termsAndConditionsSettings'
  >('termsAndConditionsSettings', queryResult);
}
